// Пульт руководителя — стартовая набора «руководство». Решение владельца 01.08.2026:
// первый экран руководства — в духе конкурсной концепции «Пульт»
// (дизайн-2026/pult/home.html): числа крупно, тренд sparkline'ом на чистом SVG,
// красный цвет только там, где нужно действие сейчас.
//
// КАК ПОДКЛЮЧАЕТСЯ. Home.tsx (диспетчер стартовых по set из /api/me) возвращает этот
// экран веткой `if (код === 'руководство')` — ветку вшивает основная сессия. Поэтому
// здесь нет ни маршрута, ни редиректа: пульт — домашний вариант внутри Home, и права
// к моменту отрисовки уже загружены (свой скелетон ниже — на случай прямой отрисовки
// в тестах).
//
// ДАННЫЕ — ТОЛЬКО ИЗ СУЩЕСТВУЮЩИХ РУЧЕК, проверенных вызовом на боевом 01.08.2026:
//   /api/recv-own/summary   дебиторка: итог, корзины срока (recv_own_api ядра);
//   crm.deal.list           открытые сделки счётом (CLOSED=N, как в кабинете);
//   /api/money/balances     касса: остатки по месяцам (money_api; видит ОДИН владелец);
//   /api/nl/summary         неликвиды: себестоимость, мёртвый груз, корзины возраста;
//   /api/health             возраст зеркала — то же число, что в «Состоянии системы».
// Тренд рисуется только там, где ручка отдаёт ряд (месяцы кассы). Ряда нет — плитки
// без тренда: выдумывать динамику из одного числа нельзя.
//
// ПЛИТКА ЗАКРЫТОГО РАЗДЕЛА НЕ РИСУЕТСЯ — тот же предикат denied(), что у меню и у
// плиток Home. Счётчик живёт внутри плитки, поэтому запросов по закрытым разделам
// пульт не шлёт вовсе; «Касса» — владельческая (раздел 'money' у одной учётки), у
// остальных её просто нет.
//
// ОШИБКА РУЧКИ НЕ РОНЯЕТ ЭКРАН: плитка показывает «—» и остаётся дверью в раздел.
// QueryState здесь не нужен по той же причине, что в Home.tsx: у плитки размером со
// спичечный коробок нет места под объяснение — раздел объяснит сам.
import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import './pult.css'

import * as api from '../../api/client'
import { Unauthorized } from '../../api/client'
import { useHealth, useRights, useRightsApi } from '../../api/hooks'
import { useОстатки } from '../../api/money'
import { useNLСвод } from '../../api/nl'
import { useRecvOwnSummary } from '../../api/recvOwn'
import { plural, pluralN } from '../../lib/format'

// Не мучить сервер повторами после конца сессии — тот же уговор, что в api/hooks.ts.
const повтор = (n: number, e: unknown) => !(e instanceof Unauthorized) && n < 2

// ─────────────────────────── число с двух метров ───────────────────────────

/** Сумма в короткой записи пульта: «41,2» + «млн ₽». Число — из ручки, тут только вид. */
function деньгиКоротко(v: number): { n: string; ед: string } {
  const абс = Math.abs(v)
  if (абс >= 1e6) {
    return { n: (v / 1e6).toLocaleString('ru-RU', { maximumFractionDigits: 1 }), ед: 'млн ₽' }
  }
  if (абс >= 1e3) return { n: Math.round(v / 1e3).toLocaleString('ru-RU'), ед: 'тыс ₽' }
  return { n: Math.round(v).toLocaleString('ru-RU'), ед: '₽' }
}

/** То же одной строкой — для пилюль и полос. */
const деньгиСтрокой = (v: number) => {
  const { n, ед } = деньгиКоротко(v)
  return `${n} ${ед}`
}

interface КПЧисло {
  /** undefined — ещё загружается («…»). */
  значение: string | undefined
  единица?: string
  /** Ошибка ручки: прочерк вместо числа, плитка остаётся дверью. */
  ошибка?: boolean
  тон?: 'ok' | 'err'
}

function КПИ({ значение, единица, ошибка, тон }: КПЧисло) {
  return (
    <div className={'pult-kpi' + (тон && !ошибка ? ` pult-${тон}` : '')}>
      {ошибка ? '—' : значение ?? '…'}
      {!ошибка && значение !== undefined && единица
        ? <span className="pult-unit">{единица}</span>
        : null}
    </div>
  )
}

// ─────────────────────────── sparkline на чистом SVG ───────────────────────────

/** Мини-тренд: статический SVG, нулевая цена отрисовки. Меньше двух точек — не рисуем. */
function Спарклайн({ ряд, подпись }: { ряд: number[]; подпись: string }) {
  if (ряд.length < 2) return null
  const w = 240
  const h = 40
  const край = 2
  const мин = Math.min(...ряд)
  const макс = Math.max(...ряд)
  const y = (v: number) =>
    макс === мин ? h / 2 : край + (h - 2 * край) * (1 - (v - мин) / (макс - мин))
  const точки = ряд
    .map((v, i) => `${Math.round((i * w) / (ряд.length - 1))},${Math.round(y(v))}`)
    .join(' ')
  return (
    <svg
      className="pult-spark"
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={подпись}
    >
      <polygon fill="var(--accent-soft)" points={`${точки} ${w},${h} 0,${h}`} />
      <polyline fill="none" stroke="var(--accent)" strokeWidth="2" points={точки} />
    </svg>
  )
}

// ─────────────────────────── плитки ───────────────────────────
// Каждая — свой компонент: хуки нельзя звать по условию, а состав плиток зависит от прав.
// Компонент отрисовывается только когда раздел человеку открыт.

/** Дебиторка из своей схемы: итог, просрочка светофором, корзины срока полосами. */
function ПлиткаДебиторка() {
  const q = useRecvOwnSummary({})
  const итог = q.data?.total
  const просроч = q.data?.buckets.find((b) => b.bucket === 'overdue')
  const макс = Math.max(...(q.data?.buckets.map((b) => b.sum) ?? []), 1)
  const кпи = итог ? деньгиКоротко(итог.sum) : undefined
  return (
    <section className="pult-tile pult-w6" aria-labelledby="pult-recv">
      <h2 id="pult-recv">Дебиторка<Link to="/receivables">раздел →</Link></h2>
      <КПИ значение={кпи?.n} единица={кпи?.ед} ошибка={q.isError} />
      <div className="pult-sub">
        {итог ? <span>{pluralN(итог.count, 'счёт', 'счёта', 'счетов')} с непогашенным остатком</span> : null}
        {просроч && просроч.sum > 0
          ? (
            <span className="pult-pill pult-err">
              просрочено {деньгиСтрокой(просроч.sum)} · {pluralN(просроч.count, 'счёт', 'счёта', 'счетов')}
            </span>
          )
          : null}
      </div>
      {q.data
        ? (
          <div className="pult-rows" aria-label="Дебиторка по срокам">
            {q.data.buckets.map((b) => {
              const горит = b.bucket === 'overdue' && b.sum > 0
              return (
                <div className="pult-row" key={b.bucket}>
                  <span className="pult-lbl">{b.label}</span>
                  <span className="pult-bar">
                    <i
                      className={горит ? 'pult-err' : undefined}
                      style={{ width: `${Math.round((b.sum / макс) * 100)}%` }}
                    />
                  </span>
                  <span className={'pult-val' + (горит ? ' pult-err' : '')}>
                    {деньгиСтрокой(b.sum)}
                  </span>
                </div>
              )
            })}
          </div>
        )
        : null}
    </section>
  )
}

/** Касса регионов: остатки по расчёту из /money/balances, тренд — месяцы того же ответа.
 *  Плитка отрисовывается только владельцу (раздел 'money'); ограничение стоит на сервере,
 *  здесь лишь не рисуем то, чего всё равно не отдадут. */
function ПлиткаКасса() {
  const q = useОстатки('?sort=ym&limit=50')
  const месяцы = useMemo(() => {
    const по = new Map<string, number>()
    for (const r of q.data?.rows ?? []) {
      if (!r.ym) continue
      по.set(r.ym, (по.get(r.ym) ?? 0) + r.calc_closing)
    }
    const ключи = [...по.keys()].sort()
    // Страница могла оборвать самый старый месяц — его сумма была бы неполной, выбрасываем.
    if (q.data?.next != null && ключи.length) ключи.shift()
    return ключи.map((ym) => ({ ym, сумма: по.get(ym) ?? 0 }))
  }, [q.data])
  const последний = месяцы.length ? месяцы[месяцы.length - 1] : undefined
  const кпи = последний ? деньгиКоротко(последний.сумма) : undefined
  return (
    <section className="pult-tile pult-w6" aria-labelledby="pult-money">
      <h2 id="pult-money">Касса регионов<Link to="/money">раздел →</Link></h2>
      <КПИ значение={кпи?.n} единица={кпи?.ед} ошибка={q.isError} />
      {последний
        ? (
          <div className="pult-sub">
            <span>по расчёту операций · {последний.ym}</span>
            <span className="pult-pill">касса приезжает из xlsx раз в месяц</span>
          </div>
        )
        : null}
      <Спарклайн
        ряд={месяцы.map((м) => м.сумма)}
        подпись="Остаток кассы по месяцам, по расчёту операций"
      />
    </section>
  )
}

/** Сделки в работе — тот же счёт CLOSED=N, что в кабинете руководителя и на плитках Home. */
function ПлиткаСделки() {
  const q = useQuery({
    queryKey: ['pult', 'deals'],
    queryFn: () => api.listDeals({ filter: { CLOSED: 'N' }, select: ['ID'] }),
    staleTime: 60_000,
    retry: повтор,
  })
  return (
    <section className="pult-tile" aria-labelledby="pult-deals">
      <h2 id="pult-deals">Сделки в работе<Link to="/deals">раздел →</Link></h2>
      <КПИ
        значение={q.data ? String(q.data.total) : undefined}
        единица={q.data ? plural(q.data.total, 'открытая', 'открытые', 'открытых') : undefined}
        ошибка={q.isError}
      />
      <div className="pult-sub">
        <span>по стадиям и воронкам — внутри раздела</span>
      </div>
    </section>
  )
}

/** Неликвиды склада: себестоимость остатка, мёртвый груз, корзины возраста полосами. */
function ПлиткаНеликвиды() {
  const q = useNLСвод({})
  const итоги = q.data?.totals
  const кпи = итоги ? деньгиКоротко(итоги.cost) : undefined
  const макс = Math.max(...(q.data?.by_age.map((b) => b.cost) ?? []), 1)
  return (
    <section className="pult-tile" aria-labelledby="pult-nl">
      <h2 id="pult-nl">Неликвиды<Link to="/nelikvid">раздел →</Link></h2>
      <КПИ значение={кпи?.n} единица={кпи?.ед} ошибка={q.isError} />
      <div className="pult-sub">
        {итоги ? <span>{pluralN(итоги.positions, 'позиция', 'позиции', 'позиций')} на складах</span> : null}
        {итоги && итоги.dead_positions > 0
          ? (
            <span className="pult-pill pult-warn">
              мёртвый груз {деньгиСтрокой(итоги.dead_cost)}
            </span>
          )
          : null}
      </div>
      {q.data
        ? (
          <div className="pult-rows" aria-label="Себестоимость по возрасту остатка">
            {q.data.by_age.filter((b) => b.n > 0).map((b) => (
              <div className="pult-row" key={b.bucket}>
                <span className="pult-lbl">{b.bucket} дн</span>
                <span className="pult-bar">
                  <i style={{ width: `${Math.round((b.cost / макс) * 100)}%` }} />
                </span>
                <span className="pult-val">{деньгиСтрокой(b.cost)}</span>
              </div>
            ))}
          </div>
        )
        : null}
    </section>
  )
}

/** Состояние системы: можно ли верить числам пульта прямо сейчас. */
function ПлиткаСистема() {
  const q = useHealth()
  return (
    <section className="pult-tile" aria-labelledby="pult-sys">
      <h2 id="pult-sys">Система<Link to="/health">раздел →</Link></h2>
      <КПИ
        значение={q.data ? (q.data.ok ? 'OK' : 'СТАРО') : undefined}
        ошибка={q.isError}
        тон={q.data ? (q.data.ok ? 'ok' : 'err') : undefined}
      />
      {q.data
        ? (
          <div className="pult-rows">
            <div className="pult-row">
              <span className="pult-lbl">возраст зеркала</span>
              <span className="pult-val">{Math.round(q.data.age_min)} мин</span>
            </div>
            <div className="pult-row">
              <span className="pult-lbl">расхождения за сутки</span>
              <span className="pult-val">{q.data.counts.diffs_24h}</span>
            </div>
          </div>
        )
        : null}
      {q.data && !q.data.ok
        ? <div className="pult-sub"><span className="pult-pill pult-err">зеркало отстало — числам верить нельзя</span></div>
        : null}
    </section>
  )
}

// ─────────────────────────── сам пульт ───────────────────────────

/** Быстрые переходы: двери в разделы руководителя, у которых нет своей плитки. */
const ПЕРЕХОДЫ: Array<{ to: string; раздел: string; t: string }> = [
  { to: '/reports', раздел: 'reports', t: 'Отчёты руководителя' },
  { to: '/analytics', раздел: 'analytics', t: 'Аналитика продаж' },
  { to: '/sales-rating', раздел: 'sales-rating', t: 'Рейтинг ОП' },
  { to: '/bank', раздел: 'bank', t: 'Банк и бухгалтерия' },
  { to: '/data-quality', раздел: 'data-quality', t: 'Качество данных' },
]

export default function HomePult() {
  const { data: права, isPending } = useRights()
  const апи = useRightsApi()

  // Через диспетчер Home права уже загружены; скелетон — на случай прямой отрисовки.
  if (isPending) return <div className="card"><div className="skeleton" style={{ height: 96 }} /></div>

  const открыт = (код: string) => !апи.denied(код)

  const плитки: JSX.Element[] = []
  if (открыт('receivables')) плитки.push(<ПлиткаДебиторка key="recv" />)
  if (открыт('money')) плитки.push(<ПлиткаКасса key="money" />)
  if (открыт('deals')) плитки.push(<ПлиткаСделки key="deals" />)
  if (открыт('nelikvid')) плитки.push(<ПлиткаНеликвиды key="nl" />)
  if (открыт('health')) плитки.push(<ПлиткаСистема key="sys" />)

  const переходы = ПЕРЕХОДЫ.filter((с) => открыт(с.раздел))

  return (
    <>
      <div className="page-head">
        <h1>{права?.set_title || 'Руководство'}</h1>
        <span className="pult-hint">что происходит и где горит — за секунду; данные зеркала, только чтение</span>
      </div>
      {плитки.length > 0
        ? <div className="pult-grid">{плитки}</div>
        : (
          <div className="empty">
            <div className="empty-title">Из этого набора вам не открыт ни один раздел</div>
            <div>Права сузили набор до нуля — это вопрос к выдавшему учётку, а не поломка.</div>
          </div>
        )}
      {переходы.length > 0
        ? (
          <nav className="pult-links" aria-label="Быстрые переходы">
            {переходы.map((с) => <Link key={с.to} to={с.to}>{с.t}</Link>)}
          </nav>
        )
        : null}
      <p className="pult-hint pult-foot">
        Красным пульт показывает только то, что требует действия; полный список открытых разделов — в меню слева.
      </p>
    </>
  )
}
